/**
 * Let every open list scroll while a dialog or sheet is open (user, 2026-10-15: "scrolling pop ups
 * like print slips in QC sample pending are not working").
 *
 * A modal Radix Dialog locks scrolling outside its content: a document-level wheel/touchmove
 * listener cancels any scroll it cannot find a scrollable box for inside the dialog. Two kinds of
 * list fall foul of it:
 *  - a native <select> (`nx-select`, appearance: base-select): its open list is the ::picker
 *    pseudo-element, so no scrollable element sits under the pointer and the wheel is cancelled;
 *  - any popover, menu or picker portaled under <body> (Radix popper content): outside the
 *    dialog's content, so the wheel over it is cancelled.
 * Combobox once fixed this for itself alone (combobox.tsx); this covers them all, once, here.
 *
 * The listener runs on window in the capture phase, ahead of the lock's, and only stops the event
 * travelling on to it. It is passive and never cancels anything, so the browser scrolls the list.
 */

/** Lists portaled out of the dialog: Radix popovers, menus, the date picker, the Combobox. */
const PORTALED_LIST =
  "[data-radix-popper-content-wrapper], [data-popup-scroll]";

function inOpenSelect(target: Element): boolean {
  // An <option> is only under the pointer while its list is open.
  if (target.closest("option, optgroup")) return true;
  const select = target.closest("select");
  if (!select) return false;
  try {
    return select.matches(":open");
  } catch {
    // A browser without :open has no base-select list either: the OS draws it, outside the page.
    return false;
  }
}

export function allowsPopupScroll(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  return inOpenSelect(target) || target.closest(PORTALED_LIST) !== null;
}

let installed = false;

export function installPopupScroll(): void {
  if (installed || typeof window === "undefined") return;
  installed = true;
  const allow = (event: Event) => {
    if (allowsPopupScroll(event.target)) event.stopPropagation();
  };
  const options = { capture: true, passive: true } as const;
  window.addEventListener("wheel", allow, options);
  window.addEventListener("touchmove", allow, options);
}
